<template>
  <div v-click-outside:[capture]="handleClose" :class="classes">
    <div ref="reference" :class="wrapClasses" @click="toggleVisible">
      <input :name="name" :value="currentValue" type="hidden" />
      <Icon :type="arrowType" :custom="customArrowType" :size="arrowSize" :class="arrowClasses"></Icon>
      <div ref="input" :tabindex="itemDisabled ? undefined : 0" :class="inputClasses" @keydown.tab="onTab" @keydown.esc="onEscape" @keydown.up="onArrow" @keydown.down="onArrow">
        <div :class="[prefixCls + '-color']">
          <div v-show="value === '' && !visible" :class="[prefixCls + '-color-empty']">
            <i :class="[iconPrefixCls, iconPrefixCls + '-ios-close']"></i>
          </div>
          <div v-show="value || visible" :style="displayedColorStyle"></div>
        </div>
      </div>
    </div>
    <transition name="transition-drop">
      <Drop ref="drop" v-transfer-dom v-show="visible" :placement="placement" :data-transfer="transfer" :transfer="transfer" style="width: 216px" :class="dropClasses">
        <transition name="fade">
          <div v-if="visible" :class="[prefixCls + '-picker']">
            <div :class="[prefixCls + '-picker-wrapper']" style="width: 216px">
              <recommend-colors v-if="colors.length" :list="colors" :class="[prefixCls + '-picker-colors']" @picker-color="handleSelectColor"></recommend-colors>
              <recommend-colors v-if="!colors.length && recommend" :list="recommendedColor" :class="[prefixCls + '-picker-colors']" @picker-color="handleSelectColor"></recommend-colors>
            </div>
            <div :class="[prefixCls + '-confirm']">
              <span :class="confirmColorClasses">
                <template v-if="editable">
                  <i-input :value="formatColor" @on-enter="handleEditColor" @on-blur="handleEditColor" size="small"></i-input>
                </template>
                <template v-else>{{ formatColor }}</template>
              </span>
              <!-- <i-button ref="clear" :tabindex="0" @click.native="handleClear" @keydown.enter="handleClear" @keydown.native.esc="closer" size="small">Clear</i-button> -->
              <i-button ref="ok" :tabindex="0" @click.native="handleSuccess" @keydown.native.tab="handleLastTab" @keydown.enter="handleSuccess" @keydown.native.esc="closer" size="small" type="primary"
                >Save</i-button
              >
            </div>
          </div>
        </transition>
      </Drop>
    </transition>
  </div>
</template>

<script>
import tinycolor from "tinycolor2";
import clickOutside from "iview/src/directives/clickoutside";
import TransferDom from "iview/src/directives/transfer-dom";
import Drop from "iview/src/components/select/dropdown.vue";
import RecommendColors from "iview/src/components/color-picker/recommend-colors.vue";

import { transferIndex, transferIncrease } from "iview/src/utils/transfer-queue";

import { oneOf } from "iview/src/utils/assist";
import Prefixes from "iview/src/components/color-picker/prefixMixin";
import { changeColor, toRGBAString } from "iview/src/components/color-picker/utils";

import iInput from "iview/src/components/input/input.vue";
import iButton from "iview/src/components/button/button.vue";
import Icon from "iview/src/components/icon/icon.vue";
import Locale from "iview/src/mixins/locale";
import Emitter from "iview/src/mixins/emitter";
export default {
  name: "DefinedColorPicker",
  components: { Drop, RecommendColors, iInput, iButton, Icon },

  directives: { clickOutside, TransferDom },

  mixins: [Emitter, Locale, Prefixes],
  props: {
    value: {
      type: String,
      default: undefined,
    },
    hue: {
      type: Boolean,
      default: true,
    },
    alpha: {
      type: Boolean,
      default: false,
    },
    recommend: {
      type: Boolean,
      default: false,
    },
    format: {
      type: String,
      validator(value) {
        return oneOf(value, ["hsl", "hsv", "hex", "rgb"]);
      },
      default: undefined,
    },
    colors: {
      type: Array,
      default() {
        return [];
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      validator(value) {
        return oneOf(value, ["small", "large", "default"]);
      },
      default() {
        return !this.$IVIEW || this.$IVIEW.size === "" ? "default" : this.$IVIEW.size;
      },
    },
    hideDropDown: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      validator(value) {
        return oneOf(value, ["top", "top-start", "top-end", "bottom", "bottom-start", "bottom-end", "left", "left-start", "left-end", "right", "right-start", "right-end"]);
      },
      default: "bottom",
    },
    transfer: {
      type: Boolean,
      default() {
        return !this.$IVIEW || this.$IVIEW.transfer === "" ? false : this.$IVIEW.transfer;
      },
    },
    name: {
      type: String,
      default: undefined,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    // 4.0.0
    capture: {
      type: Boolean,
      default() {
        return !this.$IVIEW ? true : this.$IVIEW.capture;
      },
    },
  },

  data() {
    return {
      val: changeColor(this.value),
      currentValue: this.value,
      dragging: false,
      visible: false,
      itemDisabled: false,
      recommendedColor: ["#00AECC", "#4BBF6B", "#D29034", "#B04623", "#89609E", "#0178b5", "#5ea95e", "#ffac2b", "#f06849", "#CD5091"],
    };
  },

  computed: {
    arrowClasses() {
      return [`${this.inputPrefixCls}-icon`, `${this.inputPrefixCls}-icon-normal`];
    },
    transition() {
      return oneOf(this.placement, ["bottom-start", "bottom", "bottom-end"]) ? "slide-up" : "fade";
    },
    saturationColors: {
      get() {
        return this.val;
      },
      set(newVal) {
        this.val = newVal;
        this.$emit("on-active-change", this.formatColor);
      },
    },
    classes() {
      return [
        `${this.prefixCls}`,
        {
          [`${this.prefixCls}-transfer`]: this.transfer,
        },
      ];
    },
    wrapClasses() {
      return [
        `${this.prefixCls}-rel`,
        `${this.prefixCls}-${this.size}`,
        `${this.inputPrefixCls}-wrapper`,
        `${this.inputPrefixCls}-wrapper-${this.size}`,
        {
          [`${this.prefixCls}-disabled`]: this.itemDisabled,
        },
      ];
    },
    inputClasses() {
      return [
        `${this.prefixCls}-input`,
        `${this.inputPrefixCls}`,
        `${this.inputPrefixCls}-${this.size}`,
        {
          [`${this.prefixCls}-focused`]: this.visible,
          [`${this.prefixCls}-disabled`]: this.itemDisabled,
        },
      ];
    },
    dropClasses() {
      return [
        `${this.transferPrefixCls}-no-max-height`,
        {
          [`${this.prefixCls}-transfer`]: this.transfer,
          [`${this.prefixCls}-hide-drop`]: this.hideDropDown,
        },
      ];
    },
    displayedColorStyle() {
      return { backgroundColor: toRGBAString(this.visible ? this.saturationColors.rgba : tinycolor(this.value).toRgb()) };
    },
    formatColor() {
      let { format, saturationColors } = this;

      if (format) {
        if (format === "hsl") {
          return tinycolor(saturationColors.hsl).toHslString();
        }

        if (format === "hsv") {
          return tinycolor(saturationColors.hsv).toHsvString();
        }

        if (format === "hex") {
          return saturationColors.hex;
        }

        if (format === "rgb") {
          return toRGBAString(saturationColors.rgba);
        }
      } else if (this.alpha) {
        return toRGBAString(saturationColors.rgba);
      }

      return saturationColors.hex;
    },
    confirmColorClasses() {
      return [
        `${this.prefixCls}-confirm-color`,
        {
          [`${this.prefixCls}-confirm-color-editable`]: this.editable,
        },
      ];
    },
    // 3.4.0, global setting customArrow 有值时，arrow 赋值空
    arrowType() {
      let type = "ios-arrow-down";

      if (this.$IVIEW) {
        if (this.$IVIEW.colorPicker.customArrow) {
          type = "";
        } else if (this.$IVIEW.colorPicker.arrow) {
          type = this.$IVIEW.colorPicker.arrow;
        }
      }
      return type;
    },
    // 3.4.0, global setting
    customArrowType() {
      let type = "";

      if (this.$IVIEW) {
        if (this.$IVIEW.colorPicker.customArrow) {
          type = this.$IVIEW.colorPicker.customArrow;
        }
      }
      return type;
    },
    // 3.4.0, global setting
    arrowSize() {
      let size = "";

      if (this.$IVIEW) {
        if (this.$IVIEW.colorPicker.arrowSize) {
          size = this.$IVIEW.colorPicker.arrowSize;
        }
      }
      return size;
    },
  },

  watch: {
    value(newVal) {
      this.val = changeColor(newVal);
    },
    visible(val) {
      this.val = changeColor(this.value);
      this.$refs.drop[val ? "update" : "destroy"]();
      this.$emit("on-open-change", Boolean(val));
    },
  },

  mounted() {
    this.$on("on-escape-keydown", this.closer);
    this.$on("on-dragging", this.setDragging);

    if (this.indexGetter) {
      this.$refs.drop.handleGetIndex = this.indexGetter;
    }
  },

  methods: {
    setDragging(value) {
      this.dragging = value;
    },
    handleClose(event) {
      if (this.visible) {
        if (this.dragging || event.type === "mousedown") {
          event.preventDefault();
          return;
        }

        if (this.transfer) {
          const { $el } = this.$refs.drop;
          if ($el === event.target || $el.contains(event.target)) {
            return;
          }
        }

        this.closer(event);
        return;
      }

      this.visible = false;
    },
    toggleVisible() {
      if (this.itemDisabled) {
        return;
      }

      this.visible = !this.visible;
      this.$refs.input.focus();
    },
    childChange(data) {
      this.colorChange(data);
    },
    colorChange(data, oldHue) {
      this.oldHue = this.saturationColors.hsl.h;
      this.saturationColors = changeColor(data, oldHue || this.oldHue);
    },
    closer(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.visible = false;
      this.$refs.input.focus();
    },
    handleButtons(event, value) {
      this.currentValue = value;
      this.$emit("input", value);
      this.$emit("on-change", value);
      this.dispatch("FormItem", "on-form-change", value);
      this.closer(event);
    },
    handleSuccess(event) {
      this.handleButtons(event, this.formatColor);
      this.$emit("on-pick-success");
    },
    handleClear(event) {
      this.handleButtons(event, "");
      this.$emit("on-pick-clear");
    },
    handleSelectColor(color) {
      this.val = changeColor(color);
      this.$emit("on-active-change", this.formatColor);
    },
    handleEditColor(event) {
      let value = event.target.value;
      this.handleSelectColor(value);
    },
    setDropIndexGetter(indexGetter) {
      this.$refs.drop.handleGetIndex = indexGetter;
    },
    handleFirstTab(event) {
      if (event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        this.$refs.ok.$el.focus();
      }
    },
    handleLastTab(event) {
      if (!event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        this.$refs.saturation.$el.focus();
      }
    },
    onTab(event) {
      if (this.visible) {
        event.preventDefault();
      }
    },
    onEscape(event) {
      if (this.visible) {
        this.closer(event);
      }
    },
    onArrow(event) {
      if (!this.visible) {
        event.preventDefault();
        event.stopPropagation();
        this.visible = true;
      }
    },
  },
};
</script>

<style>
.ivu-color-picker-confirm button + button {
  margin-left: 4px;
  margin-bottom: 0;
}

.ivu-color-picker-picker-colors-wrapper {
  width: 40px;
}

.ivu-color-picker-picker-colors-wrapper-color {
  width: 32px;
}

.ivu-color-picker-picker-colors-wrapper-circle {
  left: 18px;
}
</style>